<template>
  <div id="chat-preview" class="h-full" v-bar>
    <div class="grid grid-cols-5 gap-y-3 w-full h-auto max-h-full py-5 pl-5">
      <preview-item v-for="(item, i) of visibleItems"
                    :item="item"
                    :key="i"
                    :conversation-id="conversationId"
                    :conversation-type="conversationType"
                    :is-read-only="isReadOnly"
      />
    </div>
  </div>
</template>

<script>
import prepend from 'ramda/src/prepend'
import PreviewItem from './v2/PreviewItem'
import WithLoader from '@/components/common/WithLoader'
import StateTreeGenerator from '@/utils/state-tree.generator'
import previewUtils from '@/utils/preview.utils'
import stateTreeUtils from '@/utils/state-tree.utils'
import answerModule from '@/store/modules/answer.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import templateModule from '@/store/modules/template.module'
import { STATE_ID } from '@/utils/bot-json.constants'
import variablesModule from '@/store/modules/variables.module'
import VariableRefsResolver from '@/utils/variable-refs.resolver'
import AnswerRefsResolver from '@/utils/answer-refs.resolver'

export default {
  name: 'ChatPreview',
  components: { PreviewItem, WithLoader },

  props: {
    customerInput: {
      required: false,
      default: null
    },
    itemId: {
      type: String,
      required: true
    },
    isReadOnly: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      variableRefsResolver: null,
      answerRefsResolver: null
    }
  },

  watch: {
    conversationJsons: {
      deep: true,
      immediate: true,
      handler(newConversationJsons) {
        if (!newConversationJsons) return

        const stateTree = new StateTreeGenerator().generate(newConversationJsons, this.conversationType, this.conversationId)
        chatBubbleModule.setStateTree(stateTree)
      }
    },

    rootJson: {
      deep: true,
      handler(newRootJson) {
        this.variableRefsResolver.resolveVariableRefs(newRootJson)
        this.answerRefsResolver.resolveAnswerRefs(newRootJson)
      }
    },

    itemId: {
      immediate: true,
      handler(newItemId) {
        if (!newItemId) return

        variablesModule.resetVariableRefs()
        answerModule.resetAnswerRefs()
        this.variableRefsResolver = new VariableRefsResolver(this.rootJson)
        this.answerRefsResolver = new AnswerRefsResolver(this.rootJson)
      }
    }
  },

  computed: {
    bubbleSelected: chatBubbleModule.bubbleSelected,
    stateTree: chatBubbleModule.stateTree,
    conversationJsons: conversationModule.getJsons,
    conversationType: conversationModule.getType,
    conversationId: conversationModule.getId,

    visibleItems() {
      if (!this.stateTree) return []

      try {
        return this.generateVisibleItems(this.bubbleSelected, this.stateTree, this.customerInput)
      } catch (e) {
        console.error('generateVisibleItems returned error:', e)
        // the error is usually thrown in case the bubble selected is not found in state tree
        this.resetBubbleSelected()
        return this.generateVisibleItems(this.bubbleSelected, this.stateTree, this.customerInput)
      }
    },

    state() {
      if (!this.conversationId) return null

      return conversationModule.getState(STATE_ID.ROOT)
    },

    rootJson() {
      return conversationModule.getRootJson()
    }
  },

  methods: {
    generateVisibleItems(bubbleSelected, stateTree, customerInput) {
      const items = answerModule.isEmptyAnswer() ?
        previewUtils.generateVisibleItemsForEmpty(stateTree) :
        previewUtils.findVisibleItems(stateTree, bubbleSelected)

      if (customerInput) {
        return prepend(previewUtils.createCustomerBubble(customerInput, true), items)
      }

      return items
    },

    resetBubbleSelected() {
      if (!this.stateTree) {
        chatBubbleModule.selectBotBubble()
        return
      }

      stateTreeUtils.selectNode(this.stateTree)
    }
  },

  mounted() {
    this.variableRefsResolver = new VariableRefsResolver(this.rootJson)
    this.answerRefsResolver = new AnswerRefsResolver(this.rootJson)
    this.resetBubbleSelected()
  },

  destroyed() {
    this.bubbleSelected = { actionId: null, stateId: null, error: null }
    chatBubbleModule.setStateTree(null)
    chatBubbleModule.clearConditionSections()
    templateModule.resetTemplateRefs()
  }
}
</script>

<style scoped lang="scss">

#chat-preview {
  max-height: calc(100vh - 100px);

  div {
    grid-auto-rows: minmax(min-content, max-content);
  }
}

</style>
